/**
 * Admin panel HTTP client (ADMIN_FLOW.md §8.2).
 *
 * Deliberately NOT the tenant `api` instance from `@/lib/api`: that one
 * attaches the tenant JWT and refreshes it on 401. The admin session is a
 * different token signed with a different secret, kept in sessionStorage
 * (a shared team login on a shared machine should not outlive the tab),
 * and a 401 here means "go back to /admin/login", never "refresh".
 */
import axios from 'axios'
import { API_BASE_URL } from '@/lib/api'
import type {
  AdminMeta,
  Assignee,
  BugDetail,
  BugListResponse,
  BugPatch,
  RatingListResponse,
  RatingSummary,
  SuggestionItem,
  SubmissionListResponse,
  SuggestionListResponse,
  SuggestionPatch,
} from './types'

export const ADMIN_TOKEN_KEY = 'beevelope_admin_token'

export function getAdminToken(): string | null {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY)
  } catch {
    return null
  }
}

export function setAdminToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(ADMIN_TOKEN_KEY, token)
    else sessionStorage.removeItem(ADMIN_TOKEN_KEY)
  } catch {
    /* storage unavailable (private mode etc.) — session simply won't persist */
  }
}

export const adminApi = axios.create({
  baseURL: `${API_BASE_URL.replace(/\/$/, '')}/admin`,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
})

adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token) {
    config.headers = config.headers ?? {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/** Registered by the auth store so a 401 anywhere in the panel ends the session. */
let onUnauthorized: (() => void) | null = null
export function setAdminUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status
    const url: string = error?.config?.url ?? ''
    // A 401 from login itself is just bad credentials; everywhere else it is a dead session.
    if (status === 401 && !url.includes('/auth/login')) {
      onUnauthorized?.()
    }
    return Promise.reject(error)
  },
)

export interface AdminPrincipal {
  id: number
  email: string
}

export interface AdminLoginResponse {
  access_token: string
  token_type: string
  expires_in: number
  admin: AdminPrincipal
}

export const adminAuthApi = {
  login: (email: string, password: string) =>
    adminApi.post<AdminLoginResponse>('/auth/login', { email, password }),
  me: () => adminApi.get<AdminPrincipal>('/auth/me'),
}

// ----------------------------------------------------------------- bugs / meta


export interface BugListParams {
  status?: string
  module?: string
  severity?: string
  assignee?: string
  q?: string
  sort?: string
  order?: 'asc' | 'desc'
  page?: number
  page_size?: number
}

export const adminMetaApi = {
  get: () => adminApi.get<AdminMeta>('/meta'),
  addAssignee: (name: string) => adminApi.post<Assignee>('/assignees', { name }),
  retireAssignee: (id: number) => adminApi.delete<void>(`/assignees/${id}`),
}

export const adminBugsApi = {
  list: (params: BugListParams) => adminApi.get<BugListResponse>('/bugs', { params }),
  get: (id: number) => adminApi.get<BugDetail>(`/bugs/${id}`),
  patch: (id: number, patch: BugPatch) => adminApi.patch<BugDetail>(`/bugs/${id}`, patch),
  /** Streams the attachment through the API with the admin bearer (for deployments without SAS). */
  attachmentBlob: (id: number) => adminApi.get<Blob>(`/bugs/${id}/attachment`, { responseType: 'blob' }),
}


export interface SuggestionListParams {
  status?: string
  module?: string
  subtype?: string
  page?: number
  page_size?: number
}

export const adminSuggestionsApi = {
  list: (params: SuggestionListParams) => adminApi.get<SuggestionListResponse>('/suggestions', { params }),
  patch: (id: number, patch: SuggestionPatch) => adminApi.patch<SuggestionItem>(`/suggestions/${id}`, patch),
}

export interface RatingListParams {
  stars?: number
  with_reason?: boolean
  page?: number
  page_size?: number
}

export const adminRatingsApi = {
  summary: () => adminApi.get<RatingSummary>('/ratings/summary'),
  list: (params: RatingListParams) => adminApi.get<RatingListResponse>('/ratings', { params }),
}


export interface SubmissionListParams {
  type?: string
  q?: string
  page?: number
  page_size?: number
}

export const adminSubmissionsApi = {
  list: (params: SubmissionListParams) => adminApi.get<SubmissionListResponse>('/submissions', { params }),
}
